import { DateTime } from 'luxon';

import { AlertTypes } from '../../../constants';
import { T, INDICATOR } from '../../../constants/tokens';

/**
 * How the alert screens colour and word things. Shared by the Overview's
 * activity feed, the Alerts list and an alert's detail page.
 */

/** The colour of a status, by name ("Emergency"). */
export const statusColor = (name) => {
  const status = Object.values(AlertTypes).find((s) => s.getDashboardStatus() === name);
  return status ? status.getColor().main : T.mutedSoft;
};

/**
 * A dot colour for an event's tone: a status name takes that status's colour;
 * success, warning, error and accent their indicator; anything else is muted.
 */
export const toneColor = (tone) => {
  if (INDICATOR[tone]) return INDICATOR[tone];
  if (tone && tone !== 'muted') return statusColor(tone);
  return T.mutedSoft;
};

/** Statuses that are a return to normal rather than an alert. */
export const NOT_ALERTS = ['Normal', 'All Clear'];

/**
 * An event that opens an alert: a status change to an actual alert. Others
 * (texts, calls, hubs, returns to Normal) have no alert page of their own.
 */
export const opensAlert = (event) => event.getAlertUpdateID() != null &&
  (event.getType() === 'status.school' || event.getType() === 'status.room') &&
  event.getTone() != null && !NOT_ALERTS.includes(event.getTone()) && INDICATOR[event.getTone()] == null;

/** "7m 58s", "1h 03m", "2d 4h". */
export const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return s + 's';
  const m = Math.floor(s / 60);
  if (m < 60) return m + 'm ' + String(s % 60).padStart(2, '0') + 's';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h ' + String(m % 60).padStart(2, '0') + 'm';
  return Math.floor(h / 24) + 'd ' + (h % 24) + 'h';
};

/** An incident's duration, or how long it has been running. */
export const incidentDuration = (incident) => {
  if (incident.getIsActive()) {
    const started = incident.getStartedAt();
    return started ? formatDuration(-started.diffNow('seconds').seconds) + ' · ongoing' : 'ongoing';
  }
  return formatDuration(incident.getDurationSeconds());
};

/** "Sep 8, 2026 · 6:09 PM", in local time. */
export const formatWhen = (dateTime) => (dateTime
  ? dateTime.toLocal().toLocaleString(DateTime.DATE_MED) + ' · ' + dateTime.toLocal().toLocaleString(DateTime.TIME_SIMPLE)
  : '—');

/**
 * A feed time: the time alone today, the day and time this week, the date
 * otherwise.
 */
export const formatFeedTime = (dateTime) => {
  if (!dateTime) return '';
  const local = dateTime.toLocal();
  const now = DateTime.now();
  if (local.hasSame(now, 'day')) return local.toLocaleString(DateTime.TIME_SIMPLE);
  if (now.diff(local, 'days').days < 6) return local.toFormat('ccc') + ' ' + local.toLocaleString(DateTime.TIME_SIMPLE);
  return local.toLocaleString({ month: 'short', day: 'numeric' });
};

/** A timeline time, "18:09:21"; with the date when it falls on another day than `start`. */
export const formatTimelineTime = (dateTime, start) => {
  if (!dateTime) return '';
  const local = dateTime.toLocal();
  const time = local.toFormat('HH:mm:ss');
  return start && !local.hasSame(start.toLocal(), 'day') ? local.toLocaleString({ month: 'short', day: 'numeric' }) + ' ' + time : time;
};
